import { Vector4 } from "three";
import colors from "../../../../../tailwind.colors";
import { blackColor, whiteColor } from "../../../common/helpers/colors";
import { vectorizeCSSRgbString as rgbToVec } from "../helpers/vectorize";
import createBaseTexture from "../instancing/textures/createBaseTexture";

// colors
const red = colors.red[400];
const green = colors.green[700];
const yellow = colors.yellow[400];
const blue = colors.blue[700];
const purple = colors.purple[400];
const gray = colors.gray[400];

// bases
const a = "A";
const c = "C";
const g = "G";
const t = "T";
const u = "U";
const n = "N";

// compliments
const complements = {
  T: a,
  U: a,
  G: c,
  C: g,
  A: t,
  N: n,
};

// base to color index
const colorIndexMap = {
  T: 0,
  U: 0,
  A: 1,
  G: 2,
  C: 3,
  N: 4,
  R: 5,
  Y: 5,
  S: 5,
  W: 5,
  K: 5,
  M: 5,
  B: 5,
  D: 5,
  H: 5,
  V: 5,
};

// https://www.bioinformatics.org/sms/iupac.html
const iupacCodes = {
  A: [a],
  C: [c],
  G: [g],
  T: [t],
  U: [u],
  N: [n],
  R: [a, g],
  Y: [c, t],
  S: [g, c],
  W: [a, t],
  K: [g, t],
  M: [a, c],
  B: [c, g, t],
  D: [a, g, t],
  H: [a, c, t],
  V: [a, c, g],
};

const invertedIupac = Object.entries(iupacCodes)
  .map(([key, val]) => ({ [val.sort().join("")]: key }))
  .reduce((ag, i) => ({ ...ag, ...i }), {});

export const baseDict = Object.entries(iupacCodes)
  .map(([code, val]) => {
    const bases = val.sort();
    const compBases = bases.map((x) => complements[x]).sort();
    const cCode = invertedIupac[compBases.join("")];
    return {
      code,
      colorInd: colorIndexMap[code],
      complement: cCode,
    };
  })
  .reduce((ag, i) => ({ ...ag, ...{ [i.code]: i } }), {});

export const rgbScale = [red, green, yellow, blue, gray, purple];
export const complementRgbScale = [green, red, blue, yellow, gray, purple];

const chars = Object.values(baseDict)
  .map(({ code, colorInd }) => ({ code, bg: rgbScale[colorInd] }))
  .map(({ code, bg }) => ({ code, bg, char: code }));

const invertedChars = Object.values(baseDict)
  .map((ch) => ({ ...ch, bg: complementRgbScale[ch.colorInd] }))
  .map(({ code, bg, complement }) => ({ code, bg, char: complement }));

const normalTxt = createBaseTexture(chars, whiteColor);
const invertedTxt = createBaseTexture(invertedChars, blackColor);

export const {
  textureCoordsMap,
  textureCoords,
  texture: normTexture,
} = normalTxt;
export const complementTexture = invertedTxt.texture;

export const colorMap = rgbScale
  .map((x) => rgbToVec(x))
  .map((x) => new Vector4(...x));

export const complementColorMap = complementRgbScale
  .map((x) => rgbToVec(x))
  .map((x) => new Vector4(...x));
